import React, { useEffect, useMemo, useState } from "react";
import {
    Card, Row, Col, Select, DatePicker, Input, Button, Avatar, Tag, Table,
    Typography, Space, Statistic, Divider, message, Tooltip, Empty, Popconfirm,
} from "antd";
import {
    TeamOutlined, AimOutlined, FileSearchOutlined, ClockCircleOutlined,
    RiseOutlined, UserOutlined, MailOutlined, PhoneOutlined,
    EnvironmentOutlined, ApartmentOutlined, CalendarOutlined,
    EyeOutlined, LinkOutlined, DeleteOutlined, ArrowLeftOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { AddEmployeesService } from "../../../Services/Manage/AddEmployeesService";
import { EnquiryService } from "../../../Services/Manage/EnquiryService";
import { ConfigurationService } from "../../../Services/Manage/ConfigurationService";
import { ServicetypeService } from "../../../Services/Manage/ServicetypeService";
import { ENDPOINT } from "../../../Services/ENDPOINT";


const PRIMARY = "rgba(11, 93, 148, 0.95)";

const priorityOptions = [
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
    { value: "Urgent", label: "Urgent" },
];

const statusColor = {
    New: "blue",
    Contacted: "green",
    "Follow-up": "purple",
    "Quotation Sent": "orange",
    Negotiation: "red",
};

const StatCard = ({ icon, label, value, sub, color }) => (
    <Card size="small" styles={{ body: { padding: 10 } }} style={{ height: "100%", borderRadius: 10 }}>
        <Space align="start" size={10}>
            <div style={{
                width: 34, height: 34, borderRadius: 9,
                background: `${color}1a`, color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, flexShrink: 0,
            }}>
                {icon}
            </div>
            <div>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>{label}</Typography.Text>
                <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
                {sub && <Typography.Text type="secondary" style={{ fontSize: 10 }}>{sub}</Typography.Text>}
            </div>
        </Space>
    </Card>
);

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const fileBase = (ENDPOINT.AddEmployees || "").replace(/\/api\/.*$/, "").replace(/\/$/, "");
const photoUrl = (p) => {
    if (!p) return undefined;
    if (/^https?:\/\//i.test(p)) return p;
    return `${fileBase}/${String(p).replace(/^\//, "")}`;
};


const AssignSalesExecutive = () => {
    const perm = getPermission("/assign-sales-executive");
    const navigate = useNavigate();
    const [msg, msgCtx] = message.useMessage();

const [executives, setExecutives] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [leadSources, setLeadSources] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [territories, setTerritories] = useState([]);
const [allServices, setAllServices] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [selectedRequirementItems, setSelectedRequirementItems] = useState([]);
    const [wonStatuses, setWonStatuses] = useState([]);
    const [quotations, setQuotations] = useState([]);

    const [selectedExecId, setSelectedExecId] = useState(null);
    const [assigning, setAssigning] = useState(false);

    // Assign form state
    const [form, setForm] = useState({
        enquiry_e_id: null,
        lead_source: null,
        lead_status: null,
        enquiry_date: null,
        territory: null,
        requirement: null,
        assign_date: dayjs(),
        priority: null,
        remarks: "",
    });
    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const loadExecutives = async () => {
        try {
            const res = await AddEmployeesService.getByDepartment("Sales");
            setExecutives(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadEnquiries = async () => {
        try {
            const res = await EnquiryService.getAllEnquiries();
            setEnquiries(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadConfigs = async () => {
        try {
            const [src, st, terr] = await Promise.all([
                ConfigurationService.getByType("lead_source"),
                ConfigurationService.getByType("enquiry_status"),
                ConfigurationService.getByType("territory"),
            ]);
            setLeadSources((src.data || []).map((i) => ({ value: i.label, label: i.label })));
            setStatuses((st.data || []).map((i) => ({ value: i.label, label: i.label })));
            setTerritories((terr.data || []).map((i) => ({ value: i.label, label: i.label })));
            setWonStatuses(
                (st.data || [])
                    .filter((item) => item.label.trim().toLowerCase() === "won")
                    .map((item) => item.label.trim())
            );
        } catch (e) { console.error(e); }
    };

const loadServicesAndProducts = async () => {
        try {
            const [svcRes, prodRes] = await Promise.all([
                ServicetypeService.getAllServices(),
                ServicetypeService.getAllProducts(),
            ]);
            setAllServices((svcRes.data || []).filter((s) => s.status === "Active"));
            setAllProducts((prodRes.data || []).filter((p) => p.status === "Active"));
        } catch (e) { console.error(e); }
    };

    const loadQuotations = async () => {
        try {
            const { QuotationService } = await import("../../../Services/Manage/QuotationService");
            const res = await QuotationService.getAllQuotation();
            setQuotations(res.data || []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        loadExecutives();
        loadEnquiries();
        loadConfigs();
        loadServicesAndProducts();
        loadQuotations();
    }, []);

    const execOptions = executives.map((e) => ({
        value: String(e.id),
        label: `${e.name}${e.uid ? ` (${e.uid})` : ""}`,
    }));

    const selectedExec = executives.find((e) => String(e.id) === String(selectedExecId)) || null;

  const convertedQuotations = useMemo(
        () => quotations.filter((q) => (q.quotation_status || "").toLowerCase() === "converted"),
        [quotations]
    );

    const overallConversionRate = enquiries.length
        ? ((convertedQuotations.length / enquiries.length) * 100).toFixed(2)
        : "0.00";

    const enquiryOptionsFlat = enquiries.map((e) => ({
        value: String(e.e_id),
        label: `${e.eq_id || ""} — ${e.name || "Lead"}${e.assigned_to ? " (Assigned)" : ""}`,
        raw: e,
    }));

    const enquiryOptions = [
        {
            label: "Unassigned",
            title: "Unassigned",
            options: enquiryOptionsFlat.filter((o) => !o.raw.assigned_to),
        },
        {
            label: "Assigned",
            title: "Assigned",
            options: enquiryOptionsFlat.filter((o) => o.raw.assigned_to),
        },
    ];

    const requirementOptions = [
        {
            label: "Services",
            title: "Services",
            options: allServices.map((s) => ({
                value: `service-${s.id}`,
                label: s.service_name,
            })),
        },
        {
            label: "Products",
            title: "Products",
            options: allProducts.map((p) => ({
                value: `product-${p.id}`,
                label: p.name,
            })),
        },
    ];

    // Recently assigned: only enquiries that have an assigned_to value, filtered by selected executive when one is picked
    const recentlyAssigned = useMemo(() => {
        const assignedOnly = enquiries.filter((e) => e.assigned_to);
        if (!selectedExec) {
            return assignedOnly.slice(0, 8);
        }
        return assignedOnly
            .filter((e) =>
                (e.assigned_to || "")
                    .split(",")
                    .map((s) => s.trim())
                    .includes(selectedExec.name))
            .slice(0, 8);
    }, [enquiries, selectedExec]);

// Derived stats for the selected executive. wonDeals now comes from
    // converted quotations, matching the won-deal definition used elsewhere.
    const execStats = useMemo(() => {
        if (!selectedExec) return null;
        const mine = enquiries.filter((e) =>
            (e.assigned_to || "")
                .split(",")
                .map((s) => s.trim())
                .includes(selectedExec.name));
        const leadsAssigned = mine.length;
        const quotationsSent = mine.filter((e) =>
            (e.eq_status || "").toLowerCase().includes("quotation")).length;
        const wonDeals = convertedQuotations.filter((q) => {
            const names = (q.assigned_to || "").split(",").map((s) => s.trim()).filter(Boolean);
            return names.includes(selectedExec.name);
        }).length;
        const followUpsDone = mine.filter((e) => e.followup_current_date || e.n_follow_up).length;
        const revenue = mine.reduce((s, e) => s + Number(e.total_amount || 0), 0);
        const achievement = leadsAssigned ? Math.round((wonDeals / leadsAssigned) * 100) : 0;
        return { leadsAssigned, followUpsDone, quotationsSent, wonDeals, revenue, achievement };
    }, [enquiries, selectedExec, convertedQuotations]);

    // Lead source mapping for the selected exec
    const leadSourceMapping = useMemo(() => {
        if (!selectedExec) return [];
        const mine = enquiries.filter((e) =>
            (e.assigned_to || "")
                .split(",")
                .map((s) => s.trim())
                .includes(selectedExec.name));
        const map = {};
        mine.forEach((e) => {
            const src = e.eq_source || "Others";
            map[src] = (map[src] || 0) + 1;
        });
        return Object.entries(map).map(([source, count]) => ({ source, count }));
    }, [enquiries, selectedExec]);

    const handleAssign = async () => {
        if (!form.enquiry_e_id) return msg.warning("Please select a lead / enquiry");
        if (!selectedExec) return msg.warning("Please select a sales executive");

        setAssigning(true);
        try {
            // fetch full enquiry first to avoid wiping other columns on update
            const opt = enquiryOptionsFlat.find((o) => o.value === String(form.enquiry_e_id));
            const base = opt?.raw || {};

            const selectedServiceIds = selectedRequirementItems
                .filter((v) => v.startsWith("service-"))
                .map((v) => Number(v.replace("service-", "")));
            const selectedProductIds = selectedRequirementItems
                .filter((v) => v.startsWith("product-"))
                .map((v) => v.replace("product-", ""));

            const updatedServiceItems = allServices
                .filter((s) => selectedServiceIds.includes(s.id))
                .map((s) => ({
                    service_id: s.id,
                    service_name: s.service_name,
                    unit_price: Number(s.unit_price || 0),
                    unit: s.unit || "",
                    hsn_code: "",
                }));

            const updatedProductItems = allProducts
                .filter((p) => selectedProductIds.includes(String(p.id)))
                .map((p) => ({
                    product_id: p.id,
                    product_name: p.name,
                    unit_price: Number(p.unit_price || 0),
                    unit: p.unit || "",
                    hsn_code: p.hsn_code || p.sku || p.code_name || "",
                }));

            const payload = {
                ...base,
                edit_e_id: base.e_id,
                edit_c_id: base.c_id,
                eq_no: base.eq_id,
                assigned_to: selectedExec.name,
                requirement_priority: form.priority || base.requirement_priority,
                note: form.remarks || base.note,
                service_items: updatedServiceItems,
                product_items: updatedProductItems,
            };

            const res = await EnquiryService.updateEnquiries(payload);
            if (res.status) {
                msg.success("Lead assigned successfully");
                setForm((p) => ({
                    ...p, enquiry_e_id: null, lead_source: null, lead_status: null,
                    enquiry_date: null, territory: null, requirement: null, remarks: "",
                }));
                setSelectedExecId(null);
                setSelectedRequirementItems([]);
                loadEnquiries();
            } else {
                msg.error(res.message || "Failed to assign");
            }
        } catch (e) {
            console.error(e);
            msg.error("Failed to assign lead");
        } finally {
            setAssigning(false);
        }
    };

    const handleReset = () => {
        setForm({
            enquiry_e_id: null, lead_source: null, lead_status: null,
            enquiry_date: null, territory: null, requirement: null,
            assign_date: dayjs(), priority: null, remarks: "",
        });
        setSelectedExecId(null);
        setSelectedRequirementItems([]);
    };

    const handleUnassign = async (record) => {
        try {
            // remove the selected exec's name from assigned_to (or clear all if none selected)
            const names = (record.assigned_to || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            const remaining = selectedExec
                ? names.filter((n) => n !== selectedExec.name)
                : [];

            const payload = {
                ...record,
                edit_e_id: record.e_id,
                edit_c_id: record.c_id,
                eq_no: record.eq_id,
                assigned_to: remaining.join(", "),
            };

            const res = await EnquiryService.updateEnquiries(payload);
            if (res.status) {
                msg.success("Lead unassigned");
                loadEnquiries();
            } else {
                msg.error(res.message || "Failed to unassign");
            }
        } catch (e) {
            console.error(e);
            msg.error("Failed to unassign lead");
        }
    };

    const recentColumns = [
        {
            title: "Lead / Enquiry", dataIndex: "eq_id", key: "eq_id",
            render: (v) => <Typography.Link>{v}</Typography.Link>
        },
        { title: "Lead Source", dataIndex: "eq_source", key: "eq_source", render: (v) => v || "-" },
        {
            title: "Customer Name", dataIndex: "name", key: "name",
            render: (v) => v || "-"
        },
        { title: "Territory", dataIndex: "location_city", key: "location_city", render: (v) => v || "-" },
        {
            title: "Status", dataIndex: "eq_status", key: "eq_status",
            render: (v) => v ? <Tag color={statusColor[v] || "default"}>{v}</Tag> : "-"
        },
        {
            title: "Action", key: "action", align: "center",
            render: (_, r) => (
                <Space size="small">
                    <Tooltip title={!perm.read ? "You don't have permission to view" : "View"}>
                        <Button size="small" type="text" icon={<EyeOutlined />} disabled={!perm.read}
                            onClick={() => navigate(`/enquiry/edit/${r.eq_id}`)} /></Tooltip>
                    <Tooltip title="Copy link"><Button size="small" type="text" icon={<LinkOutlined />} /></Tooltip>
                    <Popconfirm title="Unassign this lead?" okText="Yes" cancelText="No"
                        okButtonProps={{ danger: true }} disabled={!perm.delete}
                        onConfirm={() => handleUnassign(r)}>
                        <Tooltip title={!perm.delete ? "You don't have permission to delete" : "Unassign"}>
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={!perm.delete} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const compact = `
      .asx .ant-card-small > .ant-card-body { padding: 12px !important; }
      .asx .ant-card { margin-bottom: 0 !important; }
      .asx .ant-form-item { margin-bottom: 0 !important; }
    `;

    return (
        <div className="asx">
            {msgCtx}
            <style>{compact}</style>
            <Card
                style={{ marginBottom: 12 }}
                title={<Typography.Title level={4} style={{ margin: 0 }}>Assign Sales Executive</Typography.Title>}
                extra={
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back to List</Button>
                }
            >
                {/* Top stat cards */}
                <Row gutter={[10, 10]}>

                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<TeamOutlined />} color="#1677ff" label="Total Sales Executives"
                            value={executives.length}
                            sub={`Active: ${executives.filter((e) => e.status === "Active").length} · Inactive: ${executives.filter((e) => e.status !== "Active").length}`} />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<AimOutlined />} color="#52c41a" label="Leads Assigned"
                            value={enquiries.filter((e) => e.assigned_to).length} sub="Total" />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<FileSearchOutlined />} color="#fa8c16" label="Pending Assignment"
                            value={enquiries.filter((e) => !e.assigned_to).length} sub="Leads" />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                        <StatCard icon={<ClockCircleOutlined />} color="#722ed1" label="Today's Follow-ups"
                            value={enquiries.filter((e) =>
                                e.n_follow_up && dayjs(e.n_follow_up, "DD-MM-YYYY").isSame(dayjs(), "day")).length}
                            sub="Across Team" />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                        <StatCard icon={<RiseOutlined />} color="#13c2c2" label="Conversion Rate"
                            value={`${overallConversionRate}%`} sub="Won vs Total Enquiries" />
                    </Col>
                </Row>

                <Divider style={{ margin: "12px 0" }} />

                <Row gutter={12}>
                    {/* LEFT */}
                    <Col xs={24} lg={15}>
                        <Card size="small" title={<Typography.Text strong>1. Select Lead / Enquiry</Typography.Text>}
                            style={{ marginBottom: 12 }}>
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Typography.Text type="secondary">Lead / Enquiry <span style={{ color: "red" }}>*</span></Typography.Text>
                                    <Select showSearch allowClear size="large" style={{ width: "100%", marginTop: 4 }}
                                        placeholder="Search lead by name, mobile, email, company..."
                                        suffixIcon={<SearchOutlined />}
                                        value={form.enquiry_e_id}
                                        onChange={(v) => {
                                            const o = enquiryOptionsFlat.find((x) => x.value === v);
                                            setField("enquiry_e_id", v);
                                            if (o?.raw) {
                                                setField("lead_source", o.raw.eq_source || null);
                                                setField("lead_status", o.raw.eq_status || null);
                                                setField("enquiry_date", o.raw.eq_date ? dayjs(o.raw.eq_date) : null);

                                                const preChecked = [
                                                    ...(o.raw.service_items || []).map((s) => `service-${s.service_id}`),
                                                    ...(o.raw.product_items || []).map((p) => `product-${p.product_id}`),
                                                ];
                                                setSelectedRequirementItems(preChecked);
                                                const assignedNames = (o.raw.assigned_to || "")
                                                    .split(",")
                                                    .map((s) => s.trim())
                                                    .filter(Boolean);
                                                if (assignedNames.length) {
                                                    const match = executives.find((ex) => ex.name === assignedNames[0]);
                                                    setSelectedExecId(match ? String(match.id) : null);
                                                } else {
                                                    setSelectedExecId(null);
                                                }
                                            } else {
                                                setField("lead_source", null);
                                                setField("lead_status", null);
                                                setField("enquiry_date", null);
                                                setField("territory", null);
                                                setSelectedRequirementItems([]);
                                                setSelectedExecId(null);
                                            }
                                        }}
                                        options={enquiryOptions}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Typography.Text type="secondary">Lead Source</Typography.Text>
                                    <Select showSearch allowClear size="large" style={{ width: "100%", marginTop: 4 }}
                                        placeholder="Select lead source" options={leadSources}
                                        value={form.lead_source} onChange={(v) => setField("lead_source", v)}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Typography.Text type="secondary">Lead Status</Typography.Text>
                                    <Select showSearch allowClear size="large" style={{ width: "100%", marginTop: 4 }}
                                        placeholder="Select status" options={statuses}
                                        value={form.lead_status} onChange={(v) => setField("lead_status", v)}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>

                                <Col xs={24} md={8} style={{ marginTop: 12 }}>
                                    <Typography.Text type="secondary">Enquiry Date</Typography.Text>
                                    <DatePicker size="large" style={{ width: "100%", marginTop: 4 }} format="DD-MM-YYYY"
                                        placeholder="Select date" value={form.enquiry_date}
                                        onChange={(v) => setField("enquiry_date", v)} />
                                </Col>
                                <Col xs={24} md={8} style={{ marginTop: 12 }}>
                                    <Typography.Text type="secondary">Territory</Typography.Text>
                                    <Select showSearch allowClear size="large" style={{ width: "100%", marginTop: 4 }}
                                        placeholder="Select territory" options={territories}
                                        value={form.territory} onChange={(v) => setField("territory", v)}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={8} style={{ marginTop: 12 }}>
                                    <Typography.Text type="secondary">Requirement / Service</Typography.Text>
                                    <Select
                                        mode="multiple" allowClear showSearch size="large"
                                        style={{ width: "100%", marginTop: 4 }}
                                        placeholder={form.enquiry_e_id ? "Select services / products" : "Select a lead first"}
                                        disabled={!form.enquiry_e_id}
                                        options={requirementOptions}
                                        value={selectedRequirementItems}
                                        onChange={setSelectedRequirementItems}
                                        maxTagCount="responsive"
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                            </Row>
                        </Card>

                        <Card size="small" title={<Typography.Text strong>2. Assign To Sales Executive</Typography.Text>}
                            style={{ marginBottom: 12 }}>
                            <Row gutter={16}>
                                <Col xs={24} md={6}>
                                    <Typography.Text type="secondary">Sales Executive <span style={{ color: "red" }}>*</span></Typography.Text>
                                    <Select showSearch allowClear size="large" style={{ width: "100%", marginTop: 4 }}
                                        placeholder="Select sales executive" options={execOptions}
                                        value={selectedExecId} onChange={setSelectedExecId}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={6}>
                                    <Typography.Text type="secondary">Assign Date <span style={{ color: "red" }}>*</span></Typography.Text>
                                    <DatePicker size="large" style={{ width: "100%", marginTop: 4 }} format="DD-MM-YYYY"
                                        value={form.assign_date} onChange={(v) => setField("assign_date", v)} />
                                </Col>
                                <Col xs={24} md={5}>
                                    <Typography.Text type="secondary">Priority</Typography.Text>
                                    <Select showSearch allowClear size="large" style={{ width: "100%", marginTop: 4 }}
                                        placeholder="Select priority"
                                        options={priorityOptions} value={form.priority}
                                        onChange={(v) => setField("priority", v)}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={7}>
                                    <Typography.Text type="secondary">Remarks</Typography.Text>
                                    <Input size="large" style={{ marginTop: 4 }} placeholder="Enter remarks (optional)"
                                        value={form.remarks} onChange={(e) => setField("remarks", e.target.value)} />
                                </Col>
                            </Row>
                            <div style={{ textAlign: "right", marginTop: 16 }}>
                                <Button size="large" style={{ marginRight: 8 }} onClick={handleReset}>Reset</Button>
                                <Tooltip title={!perm.add ? "You don't have permission to assign" : ""}>
                                    <Button type="primary" size="large" loading={assigning} onClick={handleAssign}
                                        disabled={!perm.add}
                                        style={{ background: PRIMARY, borderColor: PRIMARY }}>Assign Lead</Button>
                                </Tooltip>
                            </div>
                        </Card>

                        <Card size="small" title={<Typography.Text strong>Recently Assigned Leads</Typography.Text>}>
                            <Table
                                rowKey="e_id"
                                size="small"
                                columns={recentColumns}
                                dataSource={recentlyAssigned}
                                pagination={{ pageSize: 5, size: "small" }}
                                locale={{ emptyText: <Empty description="No assigned leads" /> }}
                            />
                        </Card>
                    </Col>

                    {/* RIGHT */}
                    <Col xs={24} lg={9}>
                        <Card size="small" title={<Typography.Text strong>Sales Executive</Typography.Text>}
                            style={{ marginBottom: 12 }}>
                            {selectedExec ? (
                                <>
                                    <Space align="center" size={10} style={{ marginBottom: 12 }}>
                                        <Avatar size={44} icon={<UserOutlined />}
                                            src={photoUrl(selectedExec.profile_photo)} />
                                        <div>
                                            <Space size={6}>
                                                <Typography.Text strong style={{ fontSize: 15 }}>{selectedExec.name}</Typography.Text>
                                                <Tag color={selectedExec.status === "Active" ? "green" : "red"} style={{ marginInlineEnd: 0 }}>
                                                    {selectedExec.status || "—"}
                                                </Tag>
                                            </Space>
                                            <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>{selectedExec.designation || "Sales Executive"}</Typography.Text></div>
                                        </div>
                                    </Space>

                                    {[
                                        { icon: <UserOutlined />, label: "Employee ID", val: selectedExec.uid },
                                        { icon: <MailOutlined />, label: "Email", val: selectedExec.email },
                                        { icon: <PhoneOutlined />, label: "Mobile", val: selectedExec.mobile_no },
                                        { icon: <EnvironmentOutlined />, label: "Territory", val: selectedExec.territory },
                                        { icon: <ApartmentOutlined />, label: "Reporting Manager", val: selectedExec.reporting_manager },
                                        { icon: <CalendarOutlined />, label: "Date of Joining", val: selectedExec.date_of_joining ? dayjs(selectedExec.date_of_joining).format("DD MMM YYYY") : null },
                                    ].map((r, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderBottom: i < 5 ? "1px solid #f5f5f5" : "none" }}>
                                            <Space size={8}>
                                                <span style={{ color: PRIMARY, fontSize: 13 }}>{r.icon}</span>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.label}</Typography.Text>
                                            </Space>
                                            <Typography.Text strong style={{ fontSize: 12 }}>{r.val || "—"}</Typography.Text>
                                        </div>
                                    ))}

                                    <Button block size="small" style={{ marginTop: 12, color: PRIMARY, borderColor: PRIMARY }}
                                        onClick={() => navigate(`/enquiry/sales-performance/${encodeURIComponent(selectedExec.name)}`)}>
                                        View Full Profile
                                    </Button>
                                </>
                            ) : (
                                <Empty description="Select a sales executive to view details" />
                            )}
                        </Card>

                        {selectedExec && execStats && (
                            <Card size="small" style={{ marginBottom: 12 }}
                                title={<Typography.Text strong>Executive Performance ({dayjs().format("MMMM YYYY")})</Typography.Text>}>
                                <Row gutter={[8, 8]}>
                                    {[
                                        { label: "Leads Assigned", value: execStats.leadsAssigned },
                                        { label: "Follow-ups Done", value: execStats.followUpsDone },
                                        { label: "Quotations Sent", value: execStats.quotationsSent },
                                        { label: "Won Deals", value: execStats.wonDeals },
                                        { label: "Revenue (₹)", value: Number(execStats.revenue).toLocaleString("en-IN") },
                                    ].map((s, i) => (
                                        <Col span={8} key={i}>
                                            <div style={{ textAlign: "center", padding: "10px 4px", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                                                <Typography.Text type="secondary" style={{ fontSize: 11 }}>{s.label}</Typography.Text>
                                                <div style={{ fontSize: 17, fontWeight: 700, color: "#222", lineHeight: 1.3, marginTop: 2 }}>{s.value}</div>
                                            </div>
                                        </Col>
                                    ))}
                                    <Col span={8}>
                                        <div style={{ textAlign: "center", padding: "10px 4px", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>Achievement</Typography.Text>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: "#52c41a", lineHeight: 1.3, marginTop: 2 }}>{execStats.achievement}.00%</div>
                                            <div style={{ height: 4, background: "#f0f0f0", borderRadius: 2, marginTop: 4 }}>
                                                <div style={{ width: `${Math.min(execStats.achievement, 100)}%`, height: "100%", background: "#52c41a", borderRadius: 2 }} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {selectedExec && (
                            <Card size="small" title={<Typography.Text strong>Lead Source Mapping</Typography.Text>}>
                                {leadSourceMapping.length ? (
                                    <Table
                                        rowKey="source" size="small" pagination={false}
                                        columns={[
                                            { title: "Lead Source", dataIndex: "source", key: "source" },
                                            { title: "Count", dataIndex: "count", key: "count", align: "right" },
                                        ]}
                                        dataSource={leadSourceMapping}
                                    />
                                ) : <Empty description="No lead sources yet" />}
                            </Card>
                        )}
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default AssignSalesExecutive;